import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2,
  Database,
  Wallet,
  Plus,
  ReceiptText,
  Users,
  PackageCheck,
  HardDriveDownload,
} from 'lucide-react';
import { LargeActionButtons } from '../components/pos/LargeActionButtons';
import { ShiftManagementDialog } from '../components/pos/ShiftManagementDialog';
import { ServiceConfigurator, type ConfiguredLine } from '../components/pos/ServiceConfigurator';
import { PaymentDialog } from '../components/pos/PaymentDialog';
import { CustomerDialog } from '../components/pos/CustomerDialog';
import { LocalOrdersDialog } from '../components/pos/LocalOrdersDialog';
import { CustomerManagementDialog } from '../components/pos/CustomerManagementDialog';
import { ClaimManagementDialog } from '../components/pos/ClaimManagementDialog';
import {
  serviceRepository,
  addonRepository,
  customerRepository,
  orderItemRepository,
  paymentRepository,
} from '../db/repositories';
import type { AddOnRecord, ServiceRecord } from '../db/types';
import { ensureLocalCatalog } from '../services/catalog';
import { completeLocalOrder } from '../domain/orders/createLocalOrder';
import { PricingEngine } from '../domain/pricing';
import { ReceiptDialog } from '../components/pos/ReceiptDialog';
import { AddOnSelectionList } from '../components/pos/AddOnSelectionList';
import type { ReceiptData } from '../domain/receipts/receiptTypes';

export function POSPage({
  cashierId,
  onSignOut,
}: {
  cashierId: string;
  onSignOut: () => Promise<void>;
}) {
  const [services, setServices] = useState<ServiceRecord[]>([]);
  const [addons, setAddons] = useState<AddOnRecord[]>([]);
  const [lines, setLines] = useState<ConfiguredLine[]>([]);
  const [configuring, setConfiguring] = useState<ServiceRecord | null>(null);
  const [customer, setCustomer] = useState<{
    id?: string;
    name: string;
    mobileNumber?: string;
    address?: string;
    notes?: string;
  }>();
  const [customerOpen, setCustomerOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [ordersOpen, setOrdersOpen] = useState(false);
  const [customersOpen, setCustomersOpen] = useState(false);
  const [claimsOpen, setClaimsOpen] = useState(false);
  const [success, setSuccess] = useState<{
    orderNumber: string;
    claimCode: string;
    change: number;
  } | null>(null);
  const [shiftOpen, setShiftOpen] = useState(false);
  const [receipt, setReceipt] = useState<ReceiptData | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [error, setError] = useState('');
  const [catalogReady, setCatalogReady] = useState(false);

  useEffect(() => {
    const loadCatalog = async () => {
      const [hasCatalog, localServices, localAddons] = await Promise.all([
        ensureLocalCatalog(),
        serviceRepository.listActive(),
        addonRepository.listActive(),
      ]);
      setServices(localServices);
      setAddons(localAddons);
      setCatalogReady(hasCatalog);
      if (!hasCatalog) {
        setError(
          'No catalog is saved in this app yet. Sign in while online to download services to this device.',
        );
      } else {
        setError('');
      }
    };
    void loadCatalog();
    window.addEventListener('babs:sync-changed', loadCatalog);
    return () => window.removeEventListener('babs:sync-changed', loadCatalog);
  }, []);
  const totalCentavos = useMemo(
    () =>
      lines.reduce(
        (sum, line) =>
          sum +
          PricingEngine.calculate({
            service: line.service,
            quantity: line.quantity,
            weightKg: line.weightKg,
            items: line.items,
            addOns: line.addOns,
          }).grandTotalCentavos,
        0,
      ),
    [lines],
  );
  const addLine = (line: ConfiguredLine) => {
    setLines((current) => [...current, line]);
    setConfiguring(null);
    setError('');
  };
  const clear = () => {
    setLines([]);
    setCustomer(undefined);
    setError('');
  };
  const complete = async (payment: {
    method: 'CASH' | 'GCASH' | 'MAYA';
    amountCentavos: number;
    reference?: string;
  }) => {
    const result = await completeLocalOrder({ lines, customer, payment, cashierId });
    const [savedCustomer, items, payments] = await Promise.all([
      result.order.customerId ? customerRepository.getById(result.order.customerId) : undefined,
      orderItemRepository.listByOrderId(result.order.id),
      paymentRepository.listByOrderId(result.order.id),
    ]);
    setReceipt({
      order: result.order,
      customer: savedCustomer,
      items,
      payments,
      businessName: 'BABS LAUNDRY SHOP',
      businessAddress: '619 Gastambide St., Sampaloc, Manila',
    });
    setPaymentOpen(false);
    setSuccess({
      orderNumber: result.order.orderNumber,
      claimCode: result.order.claimCode!,
      change: result.changeCentavos,
    });
    setRefreshKey((key) => key + 1);
    clear();
  };

  return (
    <div className="pos-shell mx-auto flex w-full max-w-[1500px] flex-col gap-3 px-3 py-3 sm:px-5 lg:px-6">
      <div className="flex shrink-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-[#00549C]">
            Cashier workspace
          </p>
          <h1 className="mt-0.5 text-2xl font-black tracking-tight text-[#003B70]">
            New laundry order
          </h1>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:justify-end">
          <button
            onClick={() => void onSignOut()}
            className="pos-touch rounded-xl border border-slate-200 bg-white px-3 text-sm font-bold text-slate-700"
          >
            Sign out
          </button>
          <button
            onClick={() => setCustomersOpen(true)}
            className="pos-touch flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-sm font-extrabold text-slate-700 shadow-sm active:scale-[0.98]"
          >
            <Users size={17} /> Customers
          </button>
          <button
            onClick={() => setClaimsOpen(true)}
            className="pos-touch flex items-center justify-center gap-2 rounded-xl border border-[#9EC6E9] bg-[#DCEEFF] px-3 text-sm font-extrabold text-[#00549C] active:scale-[0.98]"
          >
            <PackageCheck size={17} /> Claims
          </button>
          <button
            onClick={() => setOrdersOpen(true)}
            className="pos-touch flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-sm font-extrabold text-slate-700 shadow-sm active:scale-[0.98]"
          >
            <Database size={17} /> Local Orders
          </button>
          <button
            onClick={() => setShiftOpen(true)}
            className="pos-touch flex items-center justify-center gap-2 rounded-xl border border-[#9EC6E9] bg-[#DCEEFF] px-3 text-sm font-extrabold text-[#00549C] active:scale-[0.98]"
          >
            <Wallet size={17} /> Shift
          </button>
        </div>
      </div>
      <div className="pos-workspace flex min-h-0 flex-col gap-3 md:flex-row">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
          <section className="pos-services flex min-h-0 flex-1 flex-col rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-3 flex shrink-0 flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-[10px] font-extrabold uppercase tracking-widest text-[#00549C]">
                  Services
                </p>
                <h2 className="text-lg font-black text-[#003B70]">Select laundry service</h2>
              </div>
              <span className="flex items-center gap-1.5 text-xs font-bold text-[#00549C]">
                <HardDriveDownload size={15} aria-hidden="true" />
                {catalogReady ? 'Saved on this device' : 'Waiting for catalog'}
              </span>
            </div>
            {services.length === 0 && (
              <div className="mb-4 rounded-2xl border border-[#9EC6E9] bg-[#F4F9FE] p-5">
                <p className="font-bold text-[#003B70]">Catalog setup needed on this device</p>
                <p className="mt-1 text-sm text-slate-600">
                  Connect online and sign in to save the current services for offline use.
                </p>
                <Link
                  to="/owner"
                  className="mt-3 inline-flex min-h-[56px] items-center rounded-xl bg-[#00549C] px-5 font-bold text-white"
                >
                  Open Owner sign-in
                </Link>
              </div>
            )}
            <div className="pos-service-grid grid grid-cols-2 gap-3 sm:grid-cols-3">
              {services.map((service) => (
                <button
                  key={service.id}
                  onClick={() => setConfiguring(service)}
                  className="pos-service-card flex flex-col justify-between rounded-2xl border border-slate-200 bg-[#F7FAFC] p-4 text-left shadow-[0_2px_8px_rgba(0,59,112,0.03)] transition hover:border-[#78AEE0] hover:bg-[#DCEEFF] active:scale-[0.98]"
                >
                  <div className="pos-service-icon grid h-10 w-10 place-items-center rounded-xl bg-[#DCEEFF] text-[#00549C]">
                    <Plus />
                  </div>
                  <div>
                    <p className="pos-service-name text-xl font-extrabold leading-tight text-slate-800">
                      {service.name}
                    </p>
                    <p className="pos-service-price mt-1 text-base font-extrabold leading-tight text-[#00549C]">
                      {service.pricingType === 'PER_LOAD'
                        ? `${PricingEngine.formatCentavos(service.priceCentavos)} / LOAD${service.loadCapacityKg ? ` • UP TO ${service.loadCapacityKg} KG` : ''}`
                        : service.pricingType === 'PER_KG'
                          ? `${PricingEngine.formatCentavos(service.priceCentavos)} / KG`
                          : service.pricingType === 'PER_ITEM'
                            ? 'Per item'
                            : PricingEngine.formatCentavos(service.priceCentavos)}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </section>
          <LargeActionButtons onNewOrder={clear} onCustomer={() => setCustomerOpen(true)} />
        </div>
        <aside className="pos-summary flex min-h-0 w-full flex-col rounded-2xl border border-slate-200 bg-white shadow-sm md:w-[min(37%,410px)] md:shrink-0">
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
            <div>
              <p className="text-[10px] font-extrabold uppercase tracking-widest text-[#00549C]">
                Current order
              </p>
              <h2 className="text-xl font-black text-[#003B70]">Order Summary</h2>
            </div>
            <ReceiptText className="text-[#00549C]" />
          </div>
          <div className="pos-summary-body min-h-0 flex-1 p-4">
            <div className="mb-3 rounded-xl border border-dashed border-[#9EC6E9] bg-[#F4F9FE] p-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-widest text-slate-400">
                  Customer
                </span>
                <button
                  onClick={() => setCustomerOpen(true)}
                  className="min-h-11 min-w-11 font-black text-[#00549C]"
                >
                  {customer ? 'Edit' : 'Add'}
                </button>
              </div>
              {customer ? (
                <>
                  <p className="mt-2 font-black text-slate-800">{customer.name}</p>
                  {customer.mobileNumber && (
                    <p className="text-xs font-semibold text-slate-500">{customer.mobileNumber}</p>
                  )}
                </>
              ) : (
                <p className="mt-2 text-sm font-semibold text-slate-400">No customer attached</p>
              )}
            </div>
            {lines.length === 0 ? (
              <div className="flex min-h-[150px] flex-col items-center justify-center text-center">
                <div className="grid h-16 w-16 place-items-center rounded-2xl bg-[#DCEEFF] text-[#00549C]">
                  <ReceiptText size={28} />
                </div>
                <p className="mt-4 font-extrabold text-slate-700">No services added</p>
                <p className="mt-1 text-xs font-medium text-slate-400">
                  Select a service to start the order.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {lines.map((line, index) => {
                  const result = PricingEngine.calculate({
                    service: line.service,
                    quantity: line.quantity,
                    weightKg: line.weightKg,
                    items: line.items,
                    addOns: line.addOns,
                  });
                  return (
                    <div
                      key={`${line.service.id}-${index}`}
                      className="rounded-xl border border-slate-100 bg-slate-50 p-3"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-extrabold text-slate-800">{line.service.name}</p>
                          <p className="mt-1 text-xs font-semibold text-slate-500">
                            {line.weightKg
                              ? `${line.weightKg} KG${line.service.pricingType === 'PER_LOAD' ? ` • ${line.quantity} load${line.quantity === 1 ? '' : 's'}` : ''}`
                              : `Qty ${line.quantity}`}
                            {line.items.length
                              ? ` • ${line.items.reduce((s, i) => s + i.quantity, 0)} items`
                              : ''}
                          </p>
                        </div>
                        <p className="font-black text-[#003B70]">
                          {PricingEngine.formatCentavos(result.grandTotalCentavos)}
                        </p>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {line.items.map((item) => (
                          <span
                            key={item.item.id}
                            className="rounded-md bg-white px-2 py-1 text-[10px] font-bold text-slate-500"
                          >
                            {item.item.name} × {item.quantity}
                          </span>
                        ))}
                        <AddOnSelectionList
                          items={PricingEngine.createAddOnSnapshots({
                            service: line.service,
                            quantity: line.quantity,
                            weightKg: line.weightKg,
                            items: line.items,
                            addOns: line.addOns,
                          }).map((snapshot) => ({
                            key: snapshot.addonId,
                            ...snapshot,
                          }))}
                          label={`${line.service.name} add-ons`}
                        />
                      </div>
                      <button
                        onClick={() => setLines((current) => current.filter((_, i) => i !== index))}
                        className="mt-2 min-h-11 px-2 text-sm font-extrabold text-red-600"
                      >
                        Remove
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="shrink-0 border-t border-slate-100 bg-white p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
            <div className="flex items-end justify-between">
              <span className="font-bold uppercase tracking-wide text-slate-500">Total</span>
              <span className="text-5xl font-black tabular-nums text-[#003B70]">
                {PricingEngine.formatCentavos(totalCentavos)}
              </span>
            </div>
            {error && (
              <p className="mt-3 rounded-xl bg-red-50 p-3 text-xs font-bold text-red-600">
                {error}
              </p>
            )}
            <button
              disabled={lines.length === 0 || totalCentavos <= 0}
              onClick={() => {
                setError('');
                setPaymentOpen(true);
              }}
              className="mt-3 min-h-[76px] w-full rounded-xl bg-[#00549C] text-lg font-black text-white shadow-sm transition active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-40"
            >
              PROCEED TO PAYMENT
            </button>
          </div>
        </aside>
      </div>
      {configuring && (
        <ServiceConfigurator
          service={configuring}
          addons={addons}
          onAdd={addLine}
          onClose={() => setConfiguring(null)}
        />
      )}
      {customerOpen && (
        <CustomerDialog
          initial={customer}
          onSave={(value) => {
            setCustomer(value);
            setCustomerOpen(false);
          }}
          onClose={() => setCustomerOpen(false)}
        />
      )}
      {paymentOpen && (
        <PaymentDialog
          totalCentavos={totalCentavos}
          onComplete={complete}
          onClose={() => setPaymentOpen(false)}
        />
      )}
      {ordersOpen && (
        <LocalOrdersDialog refreshKey={refreshKey} onClose={() => setOrdersOpen(false)} />
      )}
      {customersOpen && <CustomerManagementDialog onClose={() => setCustomersOpen(false)} />}
      {claimsOpen && (
        <ClaimManagementDialog refreshKey={refreshKey} onClose={() => setClaimsOpen(false)} />
      )}
      {receipt && <ReceiptDialog data={receipt} onClose={() => setReceipt(null)} />}
      {shiftOpen && <ShiftManagementDialog onClose={() => setShiftOpen(false)} />}
      {success && (
        <div className="fixed inset-0 z-[60] grid place-items-center bg-slate-950/40 p-5">
          <div className="w-full max-w-lg rounded-2xl bg-white p-7 text-center shadow-2xl">
            <CheckCircle2 className="mx-auto text-emerald-600" size={58} />
            <p className="mt-4 text-xs font-extrabold uppercase tracking-widest text-emerald-600">
              Saved locally
            </p>
            <h2 className="mt-1 text-2xl font-black text-[#003B70]">Order completed</h2>
            <div className="mt-5 rounded-xl bg-[#F7FAFC] p-5">
              <p className="text-xs font-bold text-slate-400">ORDER NUMBER</p>
              <p className="text-2xl font-black text-[#003B70]">{success.orderNumber}</p>
              <p className="mt-4 text-xs font-bold text-slate-400">CLAIM CODE</p>
              <p className="text-4xl font-black tracking-[0.2em] text-[#00549C]">
                {success.claimCode}
              </p>
              <p className="mt-4 text-sm font-bold text-slate-600">
                Change: {PricingEngine.formatCentavos(success.change)}
              </p>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <button
                onClick={() => {
                  setSuccess(null);
                  setReceipt(null);
                }}
                className="min-h-[60px] rounded-xl border border-slate-200 text-lg font-black text-slate-700"
              >
                START NEXT ORDER
              </button>
              <button
                onClick={() => {
                  setSuccess(null);
                }}
                className="min-h-[60px] rounded-xl bg-[#00549C] text-lg font-black text-white"
              >
                PRINT RECEIPT
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
